import React, { useState, useRef, useEffect } from 'react';
import en from '../../../translations/en.json';
import { toast } from '../../../components/shared/toast';
import './RingtoneForm.css';

function RingtoneForm({ onSave, onCancel, isModal = false, existingNames = [], operator }) {
  const [name, setName] = useState(operator ? operator.name : '');
  const [logo, setLogo] = useState(operator && operator.logo ? {
    previewUrl: operator.logo,
    name: operator.logo.split('/').pop() || 'Existing Logo',
    size: 0,
    isPDF: operator.logo.endsWith('.pdf'),
    isExisting: true
  } : null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (operator) {
      setName(operator.name || '');
      setLogo(operator.logo ? {
        previewUrl: operator.logo,
        name: operator.logo.split('/').pop() || 'Existing Logo',
        size: 0,
        isPDF: operator.logo.endsWith('.pdf'),
        isExisting: true
      } : null);
    } else {
      setName('');
      setLogo(null);
    }
  }, [operator]);

  const fileInputRef = useRef(null);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const validateFile = (file) => {
    if (!file) return false;

    // Validate size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.warning(t('ringtone.validationLogoRange') || 'Logo size must be under 5MB');
      return false;
    }

    // Validate type (png, jpg, jpeg, pdf)
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.pdf'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExtensions.includes(fileExt)) {
      toast.warning(t('ringtone.validationLogoType') || 'Unsupported file type. Only PNG, JPG, JPEG, and PDF are allowed');
      return false;
    }

    return true;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        processFile(file);
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        processFile(file);
      }
    }
  };

  const processFile = (file) => {
    const isPDF = file.name.toLowerCase().endsWith('.pdf');
    if (isPDF) {
      setLogo({
        file: file,
        name: file.name,
        size: file.size,
        previewUrl: null,
        isPDF: true
      });
    } else {
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogo({
          file: file,
          name: file.name,
          size: file.size,
          previewUrl: event.target.result,
          isPDF: false
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current.click();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleReset = () => {
    setName('');
    setLogo(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.warning(t('ringtone.validationNameRequired') || 'Operator name is required');
      return;
    }

    const nameLower = name.trim().toLowerCase();
    const isDuplicate = existingNames?.some(
      (existingName) => existingName.toLowerCase() === nameLower && (!operator || operator.name.toLowerCase() !== nameLower)
    );
    if (isDuplicate) {
      toast.warning('Ringtone account with this name already exists.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        logo: logo.isExisting ? null : logo
      };
      await onSave?.(payload);
    } catch (err) {
      toast.error(err.message || 'Failed to save ringtone operator.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="ringtone-form-wrapper">
      {/* Page Header (Only visible if not inside modal) */}
      {!isModal && (
        <div className="ringtone-page-header">
          {/* Breadcrumb Navigation */}
          <nav className="breadcrumb-nav">
            <button type="button" className="breadcrumb-link" onClick={onCancel}>
              {t('songs.breadcrumb')}
            </button>
            <span className="breadcrumb-separator">&gt;</span>
            <span className="breadcrumb-current">{t('ringtone.breadcrumb')}</span>
          </nav>

          {/* Title Header Section */}
          <div className="add-song-header-row">
            <div className="header-icon-box">
              {/* Bell Outline Icon */}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0b66e3" strokeWidth="2.5">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </div>
            <div className="header-text-container">
              <h1 className="add-song-title">{operator ? 'Edit Ringtone Operator' : t('ringtone.addNewTitle')}</h1>
              <p className="add-song-subtitle">{operator ? 'Update details of the selected operator' : t('ringtone.addNewSubtitle')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Form Fields Container */}
      <form onSubmit={handleSubmit} className="ringtone-form">
        <div className="form-card">
          {/* Name Input */}
          <div className="form-field-group">
            <label className="form-field-label">
              {t('ringtone.nameLabel')} <span className="required-asterisk">*</span>
            </label>
            <input
              type="text"
              className="form-text-input"
              placeholder={t('ringtone.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Company Image / Logo Upload */}
          <div className="form-field-group">
            <label className="form-field-label">
              {t('ringtone.imageLabel')}
            </label>

            <input
              type="file"
              ref={fileInputRef}
              accept=".png,.jpg,.jpeg,.pdf"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {!logo ? (
              <div
                className={`ringtone-upload-zone ${isDragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                onClick={triggerFileSelect}
              >
                <div className="ringtone-upload-inner">
                  {/* Cloud Icon */}
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0b66e3" strokeWidth="2.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>

                  <span className="ringtone-upload-text">{t('ringtone.imageUploadInfo')}</span>

                  <button type="button" className="ringtone-upload-btn">
                    {t('common.chooseFile')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="ringtone-image-preview-card">
                <div className="ringtone-image-preview-left">
                  {logo.isPDF ? (
                    <div className="pdf-preview-icon-box">
                      {/* PDF Document Icon */}
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10 9 9 9 8 9" />
                      </svg>
                    </div>
                  ) : (
                    <img src={logo.previewUrl} alt="Logo Preview" className="ringtone-preview-img" />
                  )}
                  <div className="ringtone-preview-details">
                    <span className="ringtone-preview-filename">{logo.name}</span>
                    <span className="ringtone-preview-filesize">{formatFileSize(logo.size)}</span>
                  </div>
                </div>

                <button
                  type="button"
                  className="remove-file-btn"
                  onClick={() => setLogo(null)}
                  aria-label="Remove uploaded file"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            )}
            <span className="ringtone-upload-sub-text">{t('ringtone.imageUploadSubInfo')}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="ringtone-form-actions">
          {isModal ? (
            <>
              <button
                type="button"
                className="form-btn-outlined"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="form-btn-primary"
                disabled={isSubmitting}
              >
                {/* Save Icon */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                <span>{isSubmitting ? 'Saving...' : t('common.save')}</span>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="form-btn-outlined"
                onClick={handleReset}
                disabled={isSubmitting}
              >
                {t('common.reset')}
              </button>
              <button
                type="submit"
                className="form-btn-primary"
                disabled={isSubmitting}
              >
                {/* Save Icon */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                <span>{isSubmitting ? 'Saving...' : t('common.save')}</span>
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

export default RingtoneForm;
